import { Flex, SearchFilterTag, Text, theme } from '@/components';
import { getSelectedRecursiveOptions } from '@/features/project/_utils/option-formatter';
import type { SharedSearchFormType } from '@/features/search/_models/shared-search';
import { useProjectsSearchStore } from '@/features/search/_stores/projects-search-store';
import { type FC, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = {
  field:
    | 'nestedOtherResponsibleOrganizationIds'
    | 'nestedPolicyIds'
    | 'nestedExpenditureBudgetPurposeIds'
    | 'nestedOrganizationIds';
};

export const CascaderFieldTag: FC<Props> = ({ field }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<SharedSearchFormType>();
  const targetValue = useWatch({ control, name: field });

  const {
    data: {
      organizationsOptions,
      policiesOptions,
      expenditureBudgetPurposesOptions,
    },
    setInputCascaderAndAccountingLabels,
  } = useProjectsSearchStore();

  const label = useMemo(() => {
    switch (field) {
      case 'nestedOrganizationIds':
        return t('organization-in-charge');
      case 'nestedOtherResponsibleOrganizationIds':
        return t('other-organization-in-charge');
      case 'nestedPolicyIds':
        return t('policy');
      case 'nestedExpenditureBudgetPurposeIds':
        return t('expenditure-budget-purpose');
    }
  }, [field, t]);

  const baseData = useMemo(() => {
    return field === 'nestedOrganizationIds' ||
      field === 'nestedOtherResponsibleOrganizationIds'
      ? organizationsOptions
      : field === 'nestedPolicyIds'
        ? policiesOptions
        : expenditureBudgetPurposesOptions;
  }, [
    field,
    organizationsOptions,
    policiesOptions,
    expenditureBudgetPurposesOptions,
  ]);

  useEffect(() => {
    if (targetValue?.length && baseData) {
      const labels = targetValue.flatMap((item) => {
        return getSelectedRecursiveOptions(baseData, [item]).at(0)?.label ?? [];
      });
      setInputCascaderAndAccountingLabels((prev) => ({
        ...prev,
        [field]: labels,
      }));
    } else {
      setInputCascaderAndAccountingLabels((prev) => ({
        ...prev,
        [field]: [],
      }));
    }
  }, [targetValue, field, baseData, setInputCascaderAndAccountingLabels]);

  if (!targetValue?.length) {
    return null;
  }

  return (
    <Flex
      gap={8}
      style={{ background: token.colorBgLayout, padding: 5 }}
      vertical
    >
      <Text variant="body" weight="regular" text={label} />
      <Flex gap={8} wrap="wrap">
        {targetValue?.map((item, index) => {
          return (
            <SearchFilterTag
              key={item.toString()}
              label={
                baseData
                  ? (getSelectedRecursiveOptions(baseData, [item]).at(0)
                      ?.label ?? t('not-exist-in-option'))
                  : t('not-exist-in-option')
              }
              closable={true}
              onClose={() => {
                setValue(
                  field,
                  targetValue.filter((_, i) => i !== index),
                );
              }}
            />
          );
        })}
      </Flex>
    </Flex>
  );
};
